import { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, TextField, Box, Typography, Chip, Stack } from '@mui/material';
import { Verified, HelpOutline } from '@mui/icons-material';
import { status as statusColors, text } from '../../theme/colors';

/**
 * Free-text agency field backed by the synced EAC register. Selecting a
 * suggestion links the realtor to that agency (green tick); a name with no
 * match is kept as agency_name_claimed so admin can verify it manually.
 */
const EacAgencyPicker = ({ value, onChange, search, disabled }) => {
  const [input, setInput] = useState(value?.agencyNameClaimed || '');
  const [agencyId, setAgencyId] = useState(value?.agencyId ?? null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const timer = useRef(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    setInput(value?.agencyNameClaimed || '');
    setAgencyId(value?.agencyId ?? null);
  }, [value?.agencyNameClaimed, value?.agencyId]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const q = input.trim();
    if (q.length < 2) { requestSeq.current += 1; setOptions([]); setSearched(false); return undefined; }
    const seq = ++requestSeq.current;
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await search(q);
        if (seq === requestSeq.current) setOptions((res?.data || []).map((r) => r.agency));
      } catch {
        if (seq === requestSeq.current) setOptions([]);
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
          setSearched(true);
        }
      }
    }, 400);
    return () => {
      clearTimeout(timer.current);
      requestSeq.current += 1;
    };
  }, [input, search]);

  const selected = useMemo(() => options.find((o) => o.id === agencyId) || null, [options, agencyId]);
  const linked = Boolean(agencyId);

  return (
    <Box>
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={options}
        loading={loading}
        value={selected}
        inputValue={input}
        filterOptions={(o) => o}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
        isOptionEqualToValue={(o, v) => o.id === v?.id}
        onInputChange={(_, v, reason) => {
          setInput(v);
          if (reason === 'input' || reason === 'clear') {
            setAgencyId(null);
            onChange({ agencyId: null, agencyNameClaimed: v, agency: null });
          }
        }}
        onChange={(_, v) => {
          if (v && typeof v === 'object') {
            setAgencyId(v.id);
            onChange({ agencyId: v.id, agencyNameClaimed: v.name, agency: v });
          }
        }}
        renderOption={(props, o) => (
          <li {...props} key={o.id}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.name}</Typography>
              <Typography variant="caption" sx={{ color: text.secondary }}>
                {o.address} · <Box component="span" sx={{ fontWeight: 700, color: /^compliant$/i.test(String(o.status || '').trim()) ? '#166534' : '#9a3412' }}>{o.status}</Box>
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Estate agency" placeholder="Start typing your agency's name"
            helperText="We match this against the Estate Agents Council register (eac.co.zw)." />
        )}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
        {linked && <Chip size="small" icon={<Verified />} label="Registered with EAC" sx={{ color: statusColors.success, bgcolor: statusColors.successBg }} />}
        {!linked && searched && input.trim().length >= 2 && !loading && (
          <Chip size="small" icon={<HelpOutline />} label="Not found on the EAC register — an admin will verify manually"
            sx={{ color: statusColors.warning, bgcolor: statusColors.warningBg }} />
        )}
      </Stack>
    </Box>
  );
};

export default EacAgencyPicker;
